import styles from './register-form.module.css';
import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { Banner } from '@/components/banner';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@/util/react-router-dom';
import { useCurrentUser } from '@/contexts/current-user-context';
import { z } from 'zod';
import { RegisterSuccessResponse } from '@/api/api.schemas';
import { Form } from '@/components/form';
import { isValidPassword, requiredString, zt } from '@/util/validators';
import { PasswordHelperList } from '@/components/auth/password-helper-list';
import { useTranslation } from '@/contexts/i18n-context';
import { useCheckUsername } from '@/api/check-username';
import { Checkbox } from '../inputs/checkbox';
import { useBranding } from '@/outlets/branding-outlet';

const registerCredentialsOnlySchema = z.object({
  username: requiredString('register.username.error'),
  password: z.string().refine(isValidPassword, { message: zt('register.password.error') }),
});

const registerCredentialsWithEmailSchema = registerCredentialsOnlySchema.extend({
  email: z.string().email({ message: zt('register.email.error') }),
});

const registerFullSchema = registerCredentialsWithEmailSchema.extend({
  firstName: requiredString('register.firstName.error'),
  lastName: requiredString('register.lastName.error'),
  marketingOptIn: z.boolean(),
});

export type RegisterCredentialsOnlyFormData = z.infer<typeof registerCredentialsOnlySchema>;
export type RegisterCredentialsWithEmailFormData = z.infer<
  typeof registerCredentialsWithEmailSchema
>;
export type RegisterFullFormData = z.infer<typeof registerFullSchema>;
type RegisterFormData = RegisterCredentialsOnlyFormData | RegisterFullFormData;

interface BaseRegisterFormProps {
  error?: string;
  loading?: boolean;
}

type RegisterFormType = 'full' | 'credentials-only' | 'credentials-with-email';

interface CredentialsOnlyRegisterFromProps extends BaseRegisterFormProps {
  type: 'credentials-only';
  onSubmit: (
    data: RegisterCredentialsOnlyFormData,
    onSuccess: (result: RegisterSuccessResponse) => void,
  ) => void;
}

interface CredentialsWithEmailRegisterFromProps extends BaseRegisterFormProps {
  type: 'credentials-with-email';
  onSubmit: (
    data: RegisterCredentialsWithEmailFormData,
    onSuccess: (result: RegisterSuccessResponse) => void,
  ) => void;
}

interface FullRegisterFromProps extends BaseRegisterFormProps {
  type: 'full';
  onSubmit: (
    data: RegisterFullFormData,
    onSuccess: (result: RegisterSuccessResponse) => void,
  ) => void;
}

type RegisterFormProps =
  | CredentialsOnlyRegisterFromProps
  | CredentialsWithEmailRegisterFromProps
  | FullRegisterFromProps;

function getRegisterFormSchema(type: RegisterFormType) {
  switch (type) {
    case 'full':
      return registerFullSchema;
    case 'credentials-only':
      return registerCredentialsOnlySchema;
    case 'credentials-with-email':
      return registerCredentialsWithEmailSchema;
    default:
      return type;
  }
}

export function RegisterForm({ error, loading, ...rest }: RegisterFormProps) {
  const { t } = useTranslation();
  const { showMarketingOptIn } = useBranding();

  const schema = getRegisterFormSchema(rest.type);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues:
      rest.type === 'full'
        ? {
            marketingOptIn: showMarketingOptIn,
          }
        : undefined,
  });

  const password = form.watch('password');

  const navigate = useNavigate();
  const { setUserData } = useCurrentUser();

  const { mutate: checkUsername } = useCheckUsername({
    mutation: {
      onSuccess: (data) => {
        if (!data.available) {
          form.setError('username', {
            message: t('register.username.notAvailable'),
          });
        }
      },
    },
  });

  const onSuccess = (result: RegisterSuccessResponse) => {
    setUserData(result.token);
    navigate('/verify-identity', {
      state: {
        method: result.twoFactorMethod,
        target: result.target,
        availableTfaMethods: result.availableTfaMethods,
      },
    });
  };

  const onSubmitHandler: SubmitHandler<RegisterFormData> = async (data) => {
    if (rest.type === 'full') {
      rest.onSubmit(data as RegisterFullFormData, onSuccess);
    } else if (rest.type === 'credentials-only') {
      rest.onSubmit(data as RegisterCredentialsOnlyFormData, onSuccess);
    } else {
      rest.onSubmit(data as RegisterCredentialsWithEmailFormData, onSuccess);
    }
  };

  return (
    <Form className={styles.form} form={form} onSubmit={onSubmitHandler}>
      {!!error && (
        <Banner type="error" focusOnMount>
          {error}
        </Banner>
      )}
      {rest.type === 'full' && (
        <>
          <TextInput
            label={t('register.firstName.label')}
            placeholder={t('register.firstName.placeholder')}
            name="firstName"
            withIconsIndicators={false}
          />
          <TextInput
            label={t('register.lastName.label')}
            placeholder={t('register.lastName.placeholder')}
            name="lastName"
            withIconsIndicators={false}
          />
        </>
      )}
      {rest.type !== 'credentials-only' && (
        <TextInput
          label={t('register.email.label')}
          placeholder={t('register.email.placeholder')}
          name="email"
          withIconsIndicators={false}
        />
      )}
      <TextInput
        label={t('register.username.label')}
        placeholder={t('register.username.placeholder')}
        name="username"
        description={t('register.username.description')}
        onBlur={(e) => checkUsername({ data: { username: e.target.value } })}
        autoComplete="username"
      />
      <div className={styles.passwordContainer}>
        <TextInput
          label={t('register.password.label')}
          type="password"
          placeholder={t('register.password.placeholder')}
          name="password"
          withIconsIndicators={false}
          autoComplete="new-password"
        />
        <PasswordHelperList password={password} />
      </div>
      {rest.type === 'full' && showMarketingOptIn && (
        <Checkbox name="marketingOptIn" label={t('register.marketingOptIn')} />
      )}
      <Button type="submit" variant="filled" color="accent" loading={loading}>
        {t('register.submitButton')}
      </Button>
    </Form>
  );
}
